/**
 * MapContent component
 * メインのマップコンテンツコンポーネント - 改良版UI統合
 */

'use client';

import React, { useCallback, useMemo, useState, useEffect } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  Node,
  Edge,
  NodeTypes,
  EdgeTypes,
  ReactFlowProvider,
  useReactFlow,
  Panel,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

import TopicNode from '@/components/map/nodes/TopicNode';
import PhaseNode from '@/components/map/nodes/PhaseNode';
import ContentNode from '@/components/map/nodes/ContentNode';
import CustomEdge from '@/components/map/edges/CustomEdge';
import PhaseEdge from '@/components/map/edges/PhaseEdge';
import ControlPanel from '@/components/map/ControlPanel';
import NodeDetailSheet from '@/components/map/NodeDetailSheet';
import EdgeDetailSheet from '@/components/map/EdgeDetailSheet';
import TranscriptPanel from '@/components/map/panels/TranscriptPanel';
import StatsPanel from '@/components/map/panels/StatsPanel';
import FloatingPanel from '@/components/map/panels/FloatingPanel';
import TimelineRuler from '@/components/map/TimelineRuler';
import TranscriptInputModal from '@/components/map/TranscriptInputModal';
import ExtractButton from '@/components/map/panels/ExtractButton';
import Flow from '@/components/map/Flow';

import { useMapState, useMapLayout, useMapExtraction, useMapUI } from '@/contexts/map';
import { TopicNode as TopicNodeType } from '@/types/nodeTypes';
import { TopicEdge } from '@/types/edgeTypes';

/**
 * NodeData interface
 */
interface NodeData {
  topicId?: string;
  [key: string]: any;
}

/**
 * LoadingView component
 */
const LoadingView: React.FC = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-foreground mx-auto mb-4" />
      <p>読み込み中...</p>
    </div>
  </div>
);

/**
 * ErrorView component
 */
const ErrorView: React.FC<{ error: Error }> = ({ error }) => (
  <div className="flex items-center justify-center h-screen">
    <div className="text-center">
      <p className="text-destructive mb-4">エラーが発生しました</p>
      <p className="mb-4">{error.message}</p>
      <Button variant="outline" onClick={() => window.location.reload()}>
        再読み込み
      </Button>
    </div>
  </div>
);

/**
 * NoDataView component
 */
const NoDataView: React.FC = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="text-center">
      <p className="mb-4">マップデータがありません</p>
      <Button variant="outline" onClick={() => window.location.reload()}>
        再読み込み
      </Button>
    </div>
  </div>
);

/**
 * MapContent component
 */
const MapContent: React.FC = () => {
  const { mapData, loading, error, updateTranscript } = useMapState();
  const { 
    filteredNodes, 
    filteredEdges, 
    selectNode, 
    selectedNode, 
    selectEdge,
    selectedEdge,
    filterVisibility,
    transcript,
    setTranscript 
  } = useMapUI();
  const { layoutType, applyLayoutToTopicNodes, generateLayoutElements } = useMapLayout();
  
  // Panel visibility state
  const [isTranscriptPanelOpen, setIsTranscriptPanelOpen] = useState(false);
  const [isStatsPanelOpen, setIsStatsPanelOpen] = useState(false);
  
  // トランスクリプト入力モーダルの状態
  const [isTranscriptModalOpen, setIsTranscriptModalOpen] = useState(false);
  
  // Generate ReactFlow elements based on current layout type
  const { reactFlowNodes, reactFlowEdges } = useMemo(() => {
    if (!mapData) {
      return { reactFlowNodes: [], reactFlowEdges: [] };
    }

    // 各レイアウトタイプに応じた処理
    // フェーズレイアウトのみを使用
    if (layoutType === 'phase') {
      // フェーズレイアウトは特殊処理
      const elements = generateLayoutElements(
        filteredNodes,
        filteredEdges,
        mapData.phases,
        layoutType,
        window.innerWidth,
        window.innerHeight
      );
      
      return {
        reactFlowNodes: elements.nodes,
        reactFlowEdges: elements.edges
      };
    }
    
    // その他のレイアウト（使用しないが、念のため実装を残す）
    const positionedNodes = applyLayoutToTopicNodes(
      filteredNodes, 
      filteredEdges,
      mapData.phases,
      layoutType
    );
    
    // Convert topic nodes to ReactFlow nodes
    const rfNodes = positionedNodes.map(node => ({
      id: node.id,
      type: 'topicNode',
      position: node.position || { x: 0, y: 0 },
      data: {
        ...node,
        width: node.width || 250,
        height: node.height || 150,
      },
    }));
    
    // Convert topic edges to ReactFlow edges
    const rfEdges = filteredEdges.map((edge: TopicEdge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      type: 'customEdge',
      data: {
        ...edge,
      },
    }));
    
    return {
      reactFlowNodes: rfNodes,
      reactFlowEdges: rfEdges
    };
  }, [mapData, filteredNodes, filteredEdges, layoutType, applyLayoutToTopicNodes, generateLayoutElements]);
  
  // マップ読み込み完了時にトランスクリプトの有無をチェックし、必要ならモーダルを表示
  useEffect(() => {
    if (mapData && !loading && !error) {
      // マップデータがあり、トランスクリプトが空の場合にモーダルを表示
      if (!mapData.transcript || mapData.transcript.trim() === '') {
        setIsTranscriptModalOpen(true);
      }
      
      // UIコンテキストにもトランスクリプトを設定
      if (mapData.transcript && !transcript) {
        setTranscript(mapData.transcript);
      }
    }
  }, [mapData, loading, error, transcript, setTranscript]);
  
  // トランスクリプトモーダルを閉じる処理
  const handleTranscriptModalClose = useCallback((newTranscript?: string) => {
    setIsTranscriptModalOpen(false);
    
    // 新しいトランスクリプトが提供された場合は保存
    if (newTranscript !== undefined) {
      setTranscript(newTranscript);
      
      // バックエンドにも保存
      if (mapData?.id) {
        updateTranscript(newTranscript);
      }
      
      // トランスクリプトパネルを自動的に開く
      setIsTranscriptPanelOpen(true);
    }
  }, [mapData, setTranscript, updateTranscript]);
  
  // Handle node click
  const handleNodeClick = useCallback((nodeId: string) => {
    // We only want to show details for topic nodes, not phase nodes
    if (!nodeId.startsWith('phase-')) {
      selectNode(nodeId);
    }
  }, [selectNode]);
  
  // Handle edge click
  const handleEdgeClick = useCallback((edgeId: string) => {
    selectEdge(edgeId);
  }, [selectEdge]);
  
  // Handle node detail sheet close
  const handleNodeDetailSheetClose = useCallback(() => {
    selectNode(null);
  }, [selectNode]);
  
  // Handle edge detail sheet close
  const handleEdgeDetailSheetClose = useCallback(() => {
    selectEdge(null);
  }, [selectEdge]);
  
  // Toggle panel visibility
  const toggleTranscriptPanel = useCallback(() => {
    setIsTranscriptPanelOpen(prev => !prev);
  }, []);
  
  const toggleStatsPanel = useCallback(() => {
    setIsStatsPanelOpen(prev => !prev);
  }, []);
  
  // Loading state
  if (loading) {
    return <LoadingView />;
  }
  
  // Error state
  if (error) {
    return <ErrorView error={error} />;
  }
  
  // No map data
  if (!mapData) {
    return <NoDataView />;
  }
  
  // ノードの状態をサマリーする
  const activeNodesCount = mapData.nodes.filter(node => node.active).length;
  const totalNodesCount = mapData.nodes.length;
  const activePercentage = Math.round((activeNodesCount / totalNodesCount) * 100);
  
  // フィルタリングによる表示ノード数
  const displayedNodesCount = filteredNodes.length;
  
  // フィルターラベルの取得
  const getFilterLabel = () => {
    switch (filterVisibility) {
      case 'active-only':
        return 'アクティブのみ';
      case 'inactive-only':
        return '非アクティブのみ';
      default:
        return '全て表示';
    }
  };
  
  return (
    <div className="relative w-screen h-screen overflow-hidden">
      {/* Full screen ReactFlow */}
      <ReactFlowProvider>
        <Flow
          initialNodes={reactFlowNodes}
          initialEdges={reactFlowEdges}
          onNodeClick={handleNodeClick}
          onEdgeClick={handleEdgeClick}
          layoutType={layoutType}
        />
      </ReactFlowProvider>
      
      {/* 右側トランスクリプトパネル */}
      <FloatingPanel
        position="right"
        isOpen={isTranscriptPanelOpen}
        onToggle={toggleTranscriptPanel}
        width="380px"
        height="80%"
      >
        <TranscriptPanel />
      </FloatingPanel>
      
      {/* 左側統計情報パネル */}
      <FloatingPanel
        position="left"
        isOpen={isStatsPanelOpen}
        onToggle={toggleStatsPanel}
        width="320px"
        height="70%"
      >
        <StatsPanel />
      </FloatingPanel>
      
      {/* 抽出開始ボタン */}
      <ExtractButton position="center" />
      
      {/* ステータスバッジ - 中央下部に表示 */}
      <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-10 flex gap-2">
        <Button 
          variant={isTranscriptPanelOpen ? "default" : "outline"} 
          onClick={toggleTranscriptPanel}
          className="shadow-md"
          size="sm"
        >
          トランスクリプト
        </Button>
        <Button 
          variant={isStatsPanelOpen ? "default" : "outline"} 
          onClick={toggleStatsPanel}
          className="shadow-md"
          size="sm"
        >
          統計情報
        </Button>
        
        <Badge 
          variant="outline" 
          className="h-8 flex items-center gap-1.5 bg-background/80 backdrop-blur-sm border shadow-md"
        >
          <div className={`w-2 h-2 rounded-full ${activePercentage > 50 ? 'bg-green-500' : 'bg-amber-500'}`} />
          抽出率: {activePercentage}% ({activeNodesCount}/{totalNodesCount})
        </Badge>
        
        <Badge 
          variant="outline" 
          className="h-8 flex items-center gap-1.5 bg-background/80 backdrop-blur-sm border shadow-md"
        >
          表示: {displayedNodesCount}ノード ({getFilterLabel()})
        </Badge>
      </div>
      
      {/* Node Detail Sheet */}
      <NodeDetailSheet
        node={selectedNode}
        isOpen={!!selectedNode}
        onClose={handleNodeDetailSheetClose}
      />
      
      {/* Edge Detail Sheet */}
      <EdgeDetailSheet
        edge={selectedEdge}
        isOpen={!!selectedEdge}
        onClose={handleEdgeDetailSheetClose}
      />
      
      {/* トランスクリプト入力モーダル */}
      <TranscriptInputModal
        isOpen={isTranscriptModalOpen}
        onClose={handleTranscriptModalClose}
        defaultTranscript={transcript}
        mapName={mapData.name}
      />
    </div>
  );
};

export default MapContent;